/**
 * @fileoverview TanStack Query hooks for the Invoices feature.
 * @module features/invoices/hooks/useInvoices
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  type UseQueryResult,
  type UseMutationResult,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from '@tanstack/react-query'
import { endOfMonth, formatISO, startOfMonth } from 'date-fns'
import {
  listInvoices,
  getInvoice,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  parseInvoiceFile,
  uploadInvoiceFile,
} from '../api/invoicesService'
import type {
  Invoice,
  InvoicePage,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  ParsedInvoiceResponse,
} from '../schemas/invoicesSchema'

const INVOICES_KEY = ['invoices'] as const

function monthRange(year: number, monthIndex: number): { start: string; end: string } {
  const s = startOfMonth(new Date(year, monthIndex, 1))
  const e = endOfMonth(s)
  const fmt = (d: Date): string => formatISO(d, { representation: 'date' })
  return { start: fmt(s), end: fmt(e) }
}

/**
 * Paginated infinite query for the invoice list, scoped to a month.
 */
export function useInvoices(
  year: number,
  monthIndex: number,
  pageSize = 20,
): UseInfiniteQueryResult<InfiniteData<InvoicePage>, Error> {
  const { start, end } = monthRange(year, monthIndex)
  return useInfiniteQuery<InvoicePage, Error>({
    queryKey: [...INVOICES_KEY, year, monthIndex, { pageSize }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const page = typeof pageParam === 'number' ? pageParam : 0
      return listInvoices({ page, size: pageSize, startDate: start, endDate: end })
    },
    getNextPageParam: lastPage => {
      if (
        typeof lastPage.number === 'number' &&
        typeof lastPage.totalPages === 'number'
      ) {
        const next = lastPage.number + 1
        return next < lastPage.totalPages ? next : undefined
      }
      return undefined
    },
  })
}

/**
 * Single invoice by UUID.
 */
export function useInvoice(uuid: string | undefined): UseQueryResult<Invoice, Error> {
  return useQuery<Invoice, Error>({
    queryKey: [...INVOICES_KEY, uuid],
    queryFn: () => getInvoice(uuid!),
    enabled: !!uuid,
  })
}

/**
 * Parse an invoice file to extract field suggestions.
 */
export function useParseInvoice(): UseMutationResult<ParsedInvoiceResponse, Error, File> {
  return useMutation<ParsedInvoiceResponse, Error, File>({
    mutationFn: parseInvoiceFile,
  })
}

/**
 * Create invoice mutation. Invalidates list on success.
 */
export function useCreateInvoice(): UseMutationResult<Invoice, Error, CreateInvoiceRequest> {
  const qc = useQueryClient()
  return useMutation<Invoice, Error, CreateInvoiceRequest>({
    mutationFn: createInvoice,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY })
    },
  })
}

/**
 * Update invoice mutation. Invalidates list and detail on success.
 */
export function useUpdateInvoice(): UseMutationResult<
  Invoice,
  Error,
  { uuid: string; body: UpdateInvoiceRequest }
> {
  const qc = useQueryClient()
  return useMutation<Invoice, Error, { uuid: string; body: UpdateInvoiceRequest }>({
    mutationFn: ({ uuid, body }) => updateInvoice(uuid, body),
    onSuccess: (_data, { uuid }) => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY })
      void qc.invalidateQueries({ queryKey: [...INVOICES_KEY, uuid] })
    },
  })
}

/**
 * Delete invoice mutation. Backend cascades the file delete and link removal;
 * we invalidate every query that depends on either side of the link.
 */
export function useDeleteInvoice(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: deleteInvoice,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY })
      void qc.invalidateQueries({ queryKey: ['payments'] })
      void qc.invalidateQueries({ queryKey: ['indirect-costs'] })
    },
  })
}

/**
 * Upload file mutation. Invalidates detail on success.
 */
export function useUploadInvoiceFile(): UseMutationResult<
  Invoice,
  Error,
  { uuid: string; file: File }
> {
  const qc = useQueryClient()
  return useMutation<Invoice, Error, { uuid: string; file: File }>({
    mutationFn: ({ uuid, file }) => uploadInvoiceFile(uuid, file),
    onSuccess: (_data, { uuid }) => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY })
      void qc.invalidateQueries({ queryKey: [...INVOICES_KEY, uuid] })
    },
  })
}
